import { Link } from 'react-router-dom';
import { getBeneficiaries, getPayments } from '@/mocks/api';
import { useAsync } from '@/hooks/useAsync';
import { resolveContext } from '@/state/session';
import { CAPABILITIES, resolveCapability } from '@/services/entitlements';
import { MobileHeader } from '@/components/mobile-shell/MobileHeader';
import { Amount, ListRow, SectionHeader, StatusBadge } from '@/components/banking';
import { EmptyState, ErrorState, LoadingRegion, RowSkeleton } from '@/components/feedback';
import { formatDayGroup } from '@/services/formatters';

/**
 * Payments list.
 *
 * Payments in flight are separated from settled history, because "where is my
 * money right now" is the question this screen exists to answer.
 */
export function PaymentsPage() {
  const resolved = resolveContext();
  const companyId = resolved?.company.id ?? null;

  const payments = useAsync(async () => {
    if (!companyId) return [];
    return (await getPayments(companyId)).data;
  }, [companyId]);

  const beneficiaries = useAsync(async () => {
    if (!companyId) return [];
    return (await getBeneficiaries(companyId)).data;
  }, [companyId]);

  if (!resolved) return null;

  const canCreate = resolveCapability(resolved.entitlements, CAPABILITIES.paymentsCreate).allowed;
  const nameFor = (id: string) =>
    (beneficiaries.data ?? []).find((b) => b.id === id)?.name ?? 'Payee';

  const list = payments.data ?? [];
  const inFlight = list.filter((p) =>
    ['awaiting-approval', 'awaiting-sca', 'processing', 'scheduled', 'draft'].includes(p.status),
  );
  const needsAction = list.filter((p) => p.status === 'failed' || p.status === 'returned');
  const settled = list.filter((p) => !inFlight.includes(p) && !needsAction.includes(p));

  const group = (title: string, rows: typeof list, id: string) =>
    rows.length === 0 ? null : (
      <section aria-labelledby={id} key={id}>
        <SectionHeader title={title} id={id} />
        <div className="tm-card tm-card--flush">
          <ul className="tm-rows">
            {rows.map((p) => (
              <li key={p.id}>
                <ListRow
                  to={`/mobile/payments/${p.id}`}
                  title={nameFor(p.beneficiaryId)}
                  meta={`${p.reference} · ${formatDayGroup(p.createdAt)}`}
                  end={
                    <span style={{ display: 'grid', gap: 4, justifyItems: 'end' }}>
                      <Amount minorUnits={p.amount} currency={p.currency} />
                      <StatusBadge status={p.status} />
                    </span>
                  }
                  ariaLabel={`Payment to ${nameFor(p.beneficiaryId)}, ${p.status}, reference ${p.reference}`}
                />
              </li>
            ))}
          </ul>
        </div>
      </section>
    );

  return (
    <>
      <MobileHeader
        title="Payments"
        root
        trailing={
          canCreate ? (
            <Link to="/mobile/payments/new" className="tm-btn tm-btn--quiet tm-btn--sm">
              Send money
            </Link>
          ) : null
        }
      />
      <div className="tm-stack">
        {payments.status === 'loading' ? (
          <LoadingRegion label="Loading payments">
            <div className="tm-card tm-card--flush">
              <RowSkeleton count={5} />
            </div>
          </LoadingRegion>
        ) : payments.status === 'error' ? (
          <ErrorState error={payments.error} onRetry={payments.reload} />
        ) : list.length === 0 ? (
          <EmptyState
            title="No payments yet"
            body="Payments you make will be listed here with their status."
            {...(canCreate
              ? {
                  action: (
                    <Link to="/mobile/payments/new" className="tm-btn">
                      Send money
                    </Link>
                  ),
                }
              : {})}
          />
        ) : (
          <>
            {group('Needs your attention', needsAction, 'pay-action')}
            {group('In progress', inFlight, 'pay-flight')}
            {group('Completed', settled, 'pay-done')}
          </>
        )}

        {canCreate && list.length > 0 ? (
          <Link to="/mobile/payments/new" className="tm-btn tm-btn--block">
            Send money
          </Link>
        ) : null}
      </div>
    </>
  );
}
